<script setup lang="ts">
import { computed } from 'vue'

import { Sizes } from '@shared/models/enums/chat-user-avatar'
import { ChatUserAvatar } from '@shared/models/interfaces/chat-user-avatar'

type AvatarBackgroundColor = 'primary' | 'secondary' | 'primarylight'
type Shape = 'circle' | 'square'
const props = withDefaults(
  defineProps<{
    avatar: ChatUserAvatar
    color: AvatarBackgroundColor
    shape?: Shape
    size?: number
  }>(),
  {
    color: 'primary',
    shape: 'square',
    size: 48,
  }
)

const initialsSize = computed(() => {
  if (props.size >= 100) {
    return 'xxxl'
  } else if (props.size >= 80) {
    return 'xxl'
  } else if (props.size >= 50) {
    return 'xl'
  }
  return 'lg'
})

const colors = computed<Record<string, string>>(() => ({
  primary: 'tw-bg-blue-100',
  secondary: 'tw-bg-gray-200',
  primarylight: 'tw-bg-blue-50',
}))
</script>

<template>
  <div :style="{ maxHeight: `${size}px` }">
    <el-avatar
      :src="avatar.src"
      :shape="shape"
      fit="contain"
      :size="size"
      data-avatar
      :class="[
        colors[color],
        {
          'app-auto-size': avatar.size !== Sizes.FILL,
        },
      ]"
      class="app-user-avatar"
    >
      <AppText
        v-if="!avatar.src"
        color="primary"
        text-style="uppercase"
        :size="initialsSize"
        data-initials
        weight="bold"
      >
        {{ avatar.initials }}
      </AppText>
    </el-avatar>
  </div>
</template>

<style scoped lang="scss">
.app-user-avatar {
  --el-avatar-border-radius: 8px;
}

:deep(.el-avatar.app-auto-size) > img {
  @apply tw-h-auto tw-w-auto;
}
</style>
